import { api } from "./apiClient.tsx";
import { setToken } from "./auth.utils.tsx";
import { handleApiError } from "./errorHandler.tsx";

import { SettingsData, ThemeOptionsType } from "../components/Settings/Settings.types";
import { ProfileData } from "../components/Profile/Profile.types";

/**
 * El backend .NET serializa el enum `Theme` con el nombre del miembro —`System`, `Light`, `Dark`—
 * y aquí el tipo es en minúscula. Sin normalizar, `resolveTheme` comparaba `"System" === "system"`,
 * nunca resolvía al tema del equipo y aplicaba `data-theme="System"`, que no casa con ninguna regla
 * de `global.css`: la app se quedaba en claro aunque el sistema estuviera en oscuro.
 *
 * Solo hace falta al LEER: el PATCH acepta minúsculas —el enlace de enums de .NET no distingue
 * mayúsculas—, así que el sentido contrario ya funcionaba.
 */
const toThemeOption = (theme: string): ThemeOptionsType => theme.toLowerCase() as ThemeOptionsType;

export const getProfileSettings = async (): Promise<SettingsData> => {
  try {
    const { data } = await api.get("/profile/settings");

    if (!data?.theme || !data?.unit_system || !data?.locale) {
      throw new Error("Profile settings data missing in response");
    }

    // El sistema de unidades viaja dentro del token, así que cambiarlo devuelve uno nuevo. Si no se
    // sustituye, el usuario sigue calculando en las unidades anteriores hasta que caduque —ocho
    // horas de cifras convertidas al revés, sin un solo error.
    if (data.access_token) {
      setToken(data.access_token);
    }

    return {
      theme: toThemeOption(data.theme),
      unit_system: data.unit_system,
      locale: data.locale,
    };
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Unauthorized access",
    });
  }
};

export const patchProfileSettings = async (settingsData: SettingsData): Promise<SettingsData> => {
  try {
    const { data } = await api.patch("/profile/settings", {
      theme: settingsData.theme,
      unit_system: settingsData.unit_system,
      locale: settingsData.locale,
    });

    if (!data?.theme || !data?.unit_system || !data?.locale) {
      throw new Error("Profile settings data missing in response");
    }

    return {
      theme: toThemeOption(data.theme),
      unit_system: data.unit_system,
      locale: data.locale,
    };
  } catch (error: any) {
    throw handleApiError(error, {
      400: "Invalid settings data",
      401: "Unauthorized access",
    });
  }
};

export const getUserProfile = async (): Promise<ProfileData> => {
  try {
    const { data } = await api.get("/profile");

    // La Api .NET devuelve el recurso plano, sin el sobre `{data:…}` de la Api Node vieja.
    const profile = data;

    if (!profile?.uuid || !profile?.email || !profile?.name) {
      throw new Error("User profile data missing in response");
    }

    return {
      uuid: profile.uuid,
      company: profile.company,
      country: profile.country,
      email: profile.email,
      name: profile.name,
      phone: profile.phone,
      permissions: Array.isArray(profile.permissions) ? profile.permissions : [],
    };
  } catch (error: any) {
    throw handleApiError(error, {
      401: "Unauthorized access",
    });
  }
};
